import React, { useState, useEffect } from 'react';
import {
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Checkbox,
  ListItemText,
  Typography,
} from '@mui/material';
import { emailValidationError, mobileDigitsOnly, mobileValidationError, normalizeEmail } from '@uniqcamp/shared';
import { useToast } from '../../contexts/ToastContext';

export interface StaffFormUser {
  _id: string;
  staffId?: string;
  name?: string;
  mobile?: string;
  email?: string;
  role?: string;
  authorityLevel?: string;
  subjects?: string[];
  isBlocked?: boolean;
}

export interface StaffFormData {
  staffId: string;
  name: string;
  mobile: string;
  email: string;
  password?: string;
  role: string;
  authorityLevel: string;
  subjects?: string[];
}

interface StaffFormProps {
  open: boolean;
  onClose: () => void;
  editingUser: StaffFormUser | null;
  schoolSubjects: string[];
  staffRolesList: { value: string; label: string }[];
  /** Resolved ladder for this school (custom or app defaults), top → down */
  authorityLevelsList: { value: string; label: string }[];
  onSave: (data: StaffFormData) => void;
  isSaving: boolean;
  canEdit: boolean;
}

export default function StaffForm({
  open,
  onClose,
  editingUser,
  schoolSubjects,
  staffRolesList,
  authorityLevelsList,
  onSave,
  isSaving,
  canEdit,
}: StaffFormProps) {
  const { showToast } = useToast();
  const defaultBottom =
    authorityLevelsList[authorityLevelsList.length - 1]?.value ?? 'LEVEL_5';
  const [staffId, setStaffId] = useState('');
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('TEACHER');
  const [authorityLevel, setAuthorityLevel] = useState(defaultBottom);
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      if (editingUser) {
        setStaffId(editingUser.staffId ?? '');
        setName(editingUser.name ?? '');
        setMobile(editingUser.mobile ?? '');
        setEmail(editingUser.email ?? '');
        setPassword('');
        setRole(editingUser.role ?? staffRolesList[0]?.value ?? 'TEACHER');
        const al = editingUser.authorityLevel || defaultBottom;
        const valid = authorityLevelsList.some((l: { value: string }) => l.value === al);
        setAuthorityLevel(valid ? al : defaultBottom);
        setSelectedSubjects(editingUser.subjects ?? []);
      } else {
        setStaffId('');
        setName('');
        setMobile('');
        setEmail('');
        setPassword('');
        setRole(staffRolesList[0]?.value ?? 'TEACHER');
        setAuthorityLevel(defaultBottom);
        setSelectedSubjects([]);
      }
    }
  }, [open, editingUser, staffRolesList, authorityLevelsList, defaultBottom]);

  const handleSubmit = () => {
    if (!name.trim()) {
      showToast('error', 'Name is required');
      return;
    }
    const emailErr = emailValidationError(email, { required: true, label: 'Email' });
    if (emailErr) {
      showToast('error', emailErr);
      return;
    }
    const mobileErr = mobileValidationError(mobile, { required: false, label: 'Mobile' });
    if (mobileErr) {
      showToast('error', mobileErr);
      return;
    }
    const subjects = role === 'TEACHER' ? selectedSubjects : undefined;
    onSave({
      staffId: staffId.trim(),
      name: name.trim(),
      mobile: mobile.trim() ? mobileDigitsOnly(mobile) : '',
      email: normalizeEmail(email),
      ...(editingUser && password ? { password } : {}),
      role,
      authorityLevel,
      subjects,
    });
  };

  const renderStaffForm = () => (
    <>
      <TextField
        fullWidth
        label="Staff ID"
        value={staffId}
        onChange={(e) => setStaffId(e.target.value)}
        margin="normal"
        placeholder="e.g. STF001"
      />
      <TextField
        fullWidth
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        margin="normal"
        required
      />
      <TextField
        fullWidth
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        margin="normal"
        required
        placeholder="staff@school.com"
        autoComplete="email"
      />
      <TextField
        fullWidth
        label="Mobile"
        value={mobile}
        onChange={(e) => setMobile(e.target.value)}
        margin="normal"
        placeholder="e.g. 9876543210 (optional)"
      />
      {!editingUser ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          They will receive an email with a link to set their password (if mail is configured). Otherwise they can use
          Forgot password on the staff login page.
        </Typography>
      ) : (
        <TextField
          fullWidth
          label="New password (leave blank to keep current)"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          margin="normal"
          placeholder="Min 6 characters to change"
          inputProps={{ minLength: 6 }}
        />
      )}
      <FormControl fullWidth margin="normal">
        <InputLabel>Role (what they do)</InputLabel>
        <Select value={role} label="Role (what they do)" onChange={(e) => setRole(e.target.value)}>
          {staffRolesList.map((r) => (
            <MenuItem key={r.value} value={r.value}>
              {r.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth margin="normal">
        <InputLabel>Authority level (seniority: top → down)</InputLabel>
        <Select
          value={authorityLevel}
          label="Authority level (seniority: top → down)"
          onChange={(e) => setAuthorityLevel(e.target.value)}
        >
          {authorityLevelsList.map((lvl: { value: string; label: string }) => (
            <MenuItem key={lvl.value} value={lvl.value}>
              {lvl.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {role === 'TEACHER' && (
        <FormControl fullWidth margin="normal">
          <InputLabel>Subject(s)</InputLabel>
          <Select
            multiple
            label="Subject(s)"
            value={selectedSubjects}
            onChange={(e) => setSelectedSubjects((e.target.value as string[]).filter((s) => schoolSubjects.includes(s)))}
            renderValue={(sel) => (sel as string[]).length ? (sel as string[]).join(', ') : 'None'}
          >
            {schoolSubjects.length === 0 ? (
              <MenuItem disabled>Add subjects in the Subjects tab first</MenuItem>
            ) : (
              schoolSubjects.map((subj) => (
                <MenuItem key={subj} value={subj}>
                  <Checkbox checked={selectedSubjects.indexOf(subj) > -1} size="small" />
                  <ListItemText primary={subj} />
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      )}
    </>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editingUser ? 'Edit Staff' : 'Add Staff'}</DialogTitle>
      <DialogContent>{renderStaffForm()}</DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {canEdit && (
          <Button variant="contained" onClick={handleSubmit} disabled={isSaving}>
            {editingUser ? 'Save' : isSaving ? 'Adding...' : 'Add'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
